import { CopyToClipboardFontVariant, truncateString, useIntl } from '@capsicum/common';
import { type RuleSet, styled } from 'styled-components';

import { useCopyToClipboard } from '../../../hooks/helpers/useCopyToClipboard';
import { CopyClipboardIcon } from '../../icons/CopyClipboard';

type CopyToClipboardProps = {
	fontVariant?: CopyToClipboardFontVariant;
	label: string;
	onCopyClick?: (value: string) => void;
	testID: string;
	value: string;
	wrapperStyle?: RuleSet<object>;
};

const MAX_VALUE_LENGTH = 20;

const CopyToClipboard = ({
	fontVariant = CopyToClipboardFontVariant.DEFAULT,
	label,
	onCopyClick,
	testID,
	value,
	wrapperStyle
}: CopyToClipboardProps) => {
	const { $t } = useIntl();
	const copiedToClipboardLabel = `${label} ${$t('wallet.digitalCollectibles.isCopied')}`;

	const { copyToClipboard } = useCopyToClipboard({
		label: copiedToClipboardLabel,
		value
	});

	return (
		<S.ItemCopyWrapper $wrapperStyle={wrapperStyle}>
			<S.ItemCopyContentWrapper
				data-testid={testID}
				onClick={() => {
					onCopyClick?.(value);
					copyToClipboard();
				}}
			>
				<S.ItemCopyText $fontVariant={fontVariant}>
					{value.length > MAX_VALUE_LENGTH ? truncateString(value, 8, 8) : value}
				</S.ItemCopyText>
			</S.ItemCopyContentWrapper>
			<S.ItemCopyIcon
				onClick={() => {
					onCopyClick?.(value);
					copyToClipboard();
				}}
			/>
		</S.ItemCopyWrapper>
	);
};

namespace S {
	export const ItemCopyWrapper = styled.div<{ $wrapperStyle?: RuleSet<object> }>`
		display: flex;
		align-items: center;
		width: 100%;
		overflow: hidden;
		white-space: nowrap;
		${({ $wrapperStyle }) => $wrapperStyle};
	`;

	export const ItemCopyContentWrapper = styled.div`
		overflow: hidden;
		cursor: pointer;
	`;

	export const ItemCopyText = styled.span<{ $fontVariant: CopyToClipboardFontVariant }>`
		width: 100%;
		text-overflow: ellipsis;
		overflow: hidden;
		${({ $fontVariant, theme }) =>
			theme.fonts.text[
				$fontVariant === CopyToClipboardFontVariant.SMALL
					? 'text4'
					: $fontVariant === CopyToClipboardFontVariant.BIG
						? 'text6'
						: 'text2'
			]};
		margin-right: ${({ theme }) => theme.spacings.xxxsmall};

		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;
	export const ItemCopyIcon = styled(CopyClipboardIcon)`
		cursor: pointer;
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;
}

export { CopyToClipboard };
